'use client'

import React, { useTransition } from 'react'
import { useSession } from 'next-auth/react'
import { MinimalisticMagnifer } from 'solar-icons'
import { searchProducts } from '@/lib/actions'
import { useSearch } from '@/lib/hooks'

import {
  CmdChip,
  Dialog,
  DialogContent,
  DialogTrigger,
  Hint,
} from '@/components/ui'
import {
  SearchBar,
  SearchResult,
  SearchResultSkeleton,
} from '@/components/molecules'
import { SearchPlaceholder } from '@/components/placeholders'
import { SearchNoProductsPlaceholder } from '@/components/placeholders/search-no-products-placeholder'

export const Search = () => {
  const { data: session } = useSession()
  const [isPending, startTransition] = useTransition()
  const [products, setProducts] = React.useState<Product[]>([])
  const {
    isSearched,
    setIsSearched,
    searchQuery,
    isModalOpen,
    setSearchQuery,
    openModal,
    closeModal,
  } = useSearch()

  if (!session) return null

  return (
    <Dialog open={isModalOpen}>
      <DialogTrigger
        onClick={openModal}
        className="group flex h-11 w-full cursor-pointer items-center gap-2 rounded-lg border border-gray-100 bg-white px-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 lg:w-full lg:flex-grow lg:shadow-card dark:border-gray-700 hover:dark:border-gray-700"
      >
        <MinimalisticMagnifer className="size-4 shrink-0" strokeWidth={2} />
        <span className="hidden text-left text-sm text-gray-400 transition-colors group-hover:text-gray-900 lg:flex lg:flex-grow">
          Search product by name, sku, or barcode
        </span>
        <span className="text-left text-sm text-gray-400 transition-colors group-hover:text-gray-900 lg:hidden lg:flex-grow">
          Search
        </span>
        <CmdChip className="hidden bg-transparent lg:flex">K</CmdChip>
      </DialogTrigger>

      <DialogContent
        closeOnOverlay
        size="screen"
        onClose={closeModal}
        closeButtonClassName="lg:top-8"
        className="lg:px-0"
      >
        <div className="flex flex-col pt-7 lg:pt-0">
          <form
            action={async () => {
              if (!searchQuery) return
              startTransition(async () => {
                const res = await searchProducts(searchQuery)
                setProducts(res)
                setIsSearched(true)
              })
            }}
            className="lg:px-6"
          >
            <SearchBar
              defaultValue={searchQuery ?? undefined}
              onChangeValue={(value) => {
                setSearchQuery(value)
                setIsSearched(false)
              }}
              className="px-1 lg:w-[calc(100%-52px)]"
            />
          </form>

          {products?.length > 0 && !isPending && (
            <div className="flex flex-col px-1 pb-2 pt-6 md:flex-row md:items-center md:justify-between lg:px-6 lg:pb-4">
              <span>Found {products?.length} products</span>
              <div className="hidden items-center gap-2 lg:flex">
                <Hint shortcut="enter" className="flex-row">
                  Select and press
                </Hint>
                <Hint shortcut="plus" className="flex-row">
                  to add one or
                </Hint>
                <span className="text-sm text-gray-600">to add multiple</span>
              </div>
            </div>
          )}

          {(!products || products?.length === 0) &&
            !isPending &&
            searchQuery &&
            isSearched && <SearchNoProductsPlaceholder />}

          {(!products || products?.length === 0) &&
            !isPending &&
            !isSearched && <SearchPlaceholder />}

          {isPending && <SearchResultSkeleton className="lg:px-6" />}

          {!isPending && products?.length > 0 && (
            <SearchResult products={products} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
